import { useEffect, useState } from "react";
import { Plus, Check, Lock, ScanLine, Copy, ArrowUp, ArrowDown, X } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import { useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import barcodeOperationApi from "../api/barcodeOperationApi";
import stitchingOperationApi from "../api/stitchingOperationApi";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Modal from "../components/Modal";
import Alert from "../components/Alert";
import Table from "../components/Table";
import Select2 from "../components/Select2";
import SearchBar from "../components/SearchBar";
import T from "../theme/colors";

const fmtAmt = n => "₹" + (Number(n) || 0).toFixed(2);
const fmtDateTime = s => s ? new Date(s).toLocaleString("en-IN",
  { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * Barcode Operations — which stitching operations each barcode needs, in what
 * line order, and the job work rate per piece of each on that barcode. The
 * operation master only names operations; the rate and the order live here.
 *
 * Stored against the PRODUCT barcode, so every sale order of the same garment
 * shares one setup; picking or scanning a production barcode finds its product
 * barcode. The Stitching Entry screen offers exactly these operations, in this
 * order, at these rates, when the barcode is scanned.
 */
export default function BarcodeOperationPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("STITCHING_OPERATION", "EDIT");
  const canDelete = hasPermission("STITCHING_OPERATION", "DELETE");
  const { mutate } = useMutation();

  const [search, setSearch] = useState("");
  const t = useServerTable(barcodeOperationApi.paged, {
    defaultSize: 25,
    debounceMs: 300,
    extraFilters: { q: search.trim() || undefined },
  });

  const [modal, setModal] = useState(null);   // { barcode } | { } for new

  const handleDelete = row => {
    if (!window.confirm(`Unlink all operations from barcode ${row.barcode}?\n\nStitching entries already `
      + `saved keep their operations and job work rates.`)) return;
    mutate(() => barcodeOperationApi.remove(row.barcode), { onSuccess: t.reload, successMsg: "Operations unlinked" });
  };

  const cols = [
    {
      key: "barcode", label: "Barcode",
      render: v => <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{v}</span>,
    },
    {
      key: "productName", label: "Product",
      render: v => v ? <span style={{ fontWeight: 600, color: "var(--text-0)" }}>{v}</span>
        : <span style={{ color: "var(--text-2)" }}>—</span>,
    },
    {
      key: "operationCount", label: "Operations",
      render: v => <span style={{ color: "var(--text-0)" }}>{v}</span>,
    },
    {
      key: "totalJobWorkRate", label: "Total Job Work Rate / Pc",
      render: v => <span style={{ fontWeight: 700, color: "var(--text-0)" }}>{fmtAmt(v)}</span>,
    },
    {
      key: "lastChanged", label: "Last Changed",
      render: v => <span style={{ fontSize: 12, color: "var(--text-2)" }}>{fmtDateTime(v)}</span>,
    },
  ];

  return (
    <div className="fade-up" style={{ padding: "20px 24px" }}>
      <PageHeader
        title="Barcode Operations"
        subtitle="The stitching operations each barcode needs, their order, and the job work rate per piece on that barcode"
        breadcrumb={["Masters", "Barcode Operations"]}
        actions={canEdit
          ? <Btn size="sm" icon={<Plus size={13} />} onClick={() => setModal({})}>Link Operations</Btn>
          : <Btn variant="secondary" size="sm" icon={<Lock size={13} />} disabled>View Only</Btn>}
      />

      <Card>
        <div style={{
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
        }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search barcode, product or operation…" />
        </div>
        <Table
          columns={cols}
          serverMode t={t}
          emptyText={search ? "No barcodes match" : "No barcode has operations yet — click Link Operations"}
          onEdit={canEdit ? row => setModal({ barcode: row.barcode }) : undefined}
          onDelete={canDelete ? handleDelete : undefined}
        />
      </Card>

      {modal && (
        <LinkModal
          initialBarcode={modal.barcode}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); t.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * Server-searched barcode picker in the scan-box style used elsewhere.
 * Scanner input works too: the scanner types the code, and it is picked from
 * the results.
 */
function BarcodePicker({ onPick, placeholder, exclude }) {
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [term, setTerm] = useState("");

  useEffect(() => {
    let alive = true;
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await barcodeOperationApi.searchBarcodes(term);
        const rows = res.data?.data || res.data || [];
        if (alive) setOpts(Array.isArray(rows) ? rows : []);
      } catch {
        if (alive) setOpts([]);
      } finally {
        if (alive) setBusy(false);
      }
    }, 200);
    return () => { alive = false; clearTimeout(h); };
  }, [term]);

  const options = opts
    .filter(o => o.barcode !== exclude)
    .map(o => ({
      value: o.barcode,
      label: `${o.barcode} — ${o.productName || "?"}`
        + (o.latestSoNumber ? ` · ${o.latestSoNumber}` : "")
        + (o.configured ? " · ✓ set" : ""),
    }));

  return (
    <Select2
      value=""
      onChange={code => { if (code) onPick(code); }}
      onSearch={setTerm}
      options={options}
      placeholder={busy ? "Searching…" : placeholder}
    />
  );
}

/**
 * Link operations to one barcode: which operations it needs, in what line
 * order, and the job work rate per piece of each. This is the only place the
 * rate and the order are set — the operation master only names operations.
 *
 * Linked operations are listed in line order (move them with the arrows) with
 * a required rate each; the rest are offered below to add. Saving replaces the
 * barcode's whole list.
 */
function LinkModal({ initialBarcode, onClose, onSaved }) {
  const { mutate, loading: saving } = useMutation();
  const [ops, setOps] = useState([]);            // active master operations, by name
  const [barcode, setBarcode] = useState(initialBarcode || "");
  const [productName, setProductName] = useState("");
  const [seq, setSeq] = useState([]);            // linked operation ids, in line order
  const [rates, setRates] = useState({});        // operationId → job work rate (text)
  const [known, setKnown] = useState({});        // operationId → { name, code, active } for linked ones
  const [err, setErr] = useState("");
  const [loadingBc, setLoadingBc] = useState(false);
  const [tried, setTried] = useState(false);     // show missing rates only after a save attempt

  useEffect(() => {
    stitchingOperationApi.active()
      .then(res => setOps(res.data?.data || res.data || []))
      .catch(e => setErr(e?.response?.data?.message || "Could not load operations"));
  }, []);

  // Loads a barcode's links — its own (to edit) or another's (to copy).
  const loadLinks = async (code, { copy = false } = {}) => {
    setErr(""); setLoadingBc(true);
    try {
      const res = await barcodeOperationApi.getByBarcode(code);
      const d = res.data?.data ?? res.data;
      const list = d.operations || [];                 // already in the barcode's order
      setSeq(list.map(o => o.operationId));
      setRates(Object.fromEntries(list.map(o => [o.operationId, String(o.jobWorkRate ?? "")])));
      setKnown(Object.fromEntries(list.map(o => [o.operationId,
        { name: o.operationName, code: o.operationCode, active: o.operationActive !== false }])));
      if (!copy) { setBarcode(d.barcode); setProductName(d.productName || ""); }
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not load that barcode");
    }
    setLoadingBc(false);
  };

  useEffect(() => {
    if (initialBarcode) loadLinks(initialBarcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBarcode]);

  const info = id => {
    const o = ops.find(x => x.id === id);
    return o ? { name: o.operationName, code: o.operationCode, active: true } : (known[id] || { name: `#${id}` });
  };
  const available = ops.filter(o => !seq.includes(o.id));

  const add = op => {
    setSeq(p => [...p, op.id]);
    setRates(p => ({ ...p, [op.id]: p[op.id] ?? "" }));
  };
  const remove = id => setSeq(p => p.filter(x => x !== id));
  const move = (i, d) => setSeq(p => {
    const j = i + d;
    if (j < 0 || j >= p.length) return p;
    const n = [...p];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const badRate = id => {
    const v = rates[id];
    return v === undefined || v === "" || Number.isNaN(Number(v)) || Number(v) < 0;
  };
  const total = seq.reduce((s, id) => s + (Number(rates[id]) || 0), 0);

  const pickBarcode = async code => {
    setBarcode(code);
    await loadLinks(code);
  };

  const save = async () => {
    if (!barcode) { setErr("Pick a barcode first"); return; }
    setTried(true);
    const missing = seq.filter(badRate).map(id => info(id).name);
    if (missing.length) {
      setErr(`Enter the job work rate per piece for ${missing.join(", ")}`);
      return;
    }
    await mutate(() => barcodeOperationApi.save({
      barcode,
      operations: seq.map((id, i) => ({ operationId: id, jobWorkRate: Number(rates[id]), sortOrder: i + 1 })),
    }), { onSuccess: onSaved, successMsg: `Operations saved for ${barcode}` });
  };

  return (
    <Modal open onClose={onClose} width={760}
      title={initialBarcode ? `Operations — ${initialBarcode}` : "Link Operations to a Barcode"}
      subtitle={productName || "Pick or scan a barcode, then add its operations in line order"}
      footer={<>
        <span style={{ marginRight: "auto", fontSize: 13, color: "var(--text-1)" }}>
          {seq.length} operation{seq.length === 1 ? "" : "s"} · Job work
          <b style={{ color: "var(--text-0)" }}> {fmtAmt(total)} / pc</b>
        </span>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={!barcode} icon={<Check size={13} />}>Save</Btn>
      </>}>
      <Alert message={err} onClose={() => setErr("")} />

      {!initialBarcode && (
        <div style={{
          display: "flex", gap: 10, alignItems: "center", marginBottom: 12,
          padding: "12px 14px", borderRadius: 8,
          background: "var(--primary-light)", border: "1px dashed var(--primary)",
        }}>
          <ScanLine size={20} style={{ color: "var(--primary)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <BarcodePicker onPick={pickBarcode} placeholder="Search or scan barcode, product or SO…" />
          </div>
          {barcode && (
            <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{barcode}</span>
          )}
        </div>
      )}

      {barcode && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Copy size={14} style={{ color: "var(--text-2)" }} />
            <span style={{ fontSize: 12.5, color: "var(--text-2)", whiteSpace: "nowrap" }}>Copy from</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BarcodePicker exclude={barcode} placeholder="another barcode's operations, order and rates…"
                onPick={code => loadLinks(code, { copy: true })} />
            </div>
          </div>

          <div style={sectionLabel}>Linked operations — in line order</div>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-input)" }}>
                  <th style={{ ...th, width: 60 }}>Order</th>
                  <th style={th}>Operation</th>
                  <th style={{ ...th, width: 190 }}>Job Work Rate / Pc *</th>
                  <th style={{ ...th, width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {loadingBc && (
                  <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-2)" }}>Loading…</td></tr>
                )}
                {!loadingBc && seq.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-2)" }}>
                    No operations linked yet — add them below in the order they are stitched.
                  </td></tr>
                )}
                {!loadingBc && seq.map((id, i) => {
                  const op = info(id);
                  const bad = tried && badRate(id);
                  return (
                    <tr key={id} style={{ borderTop: `1px solid ${T.border}`, background: "var(--bg-card)" }}>
                      <td style={{ ...td, fontWeight: 800, color: "var(--primary)", textAlign: "center" }}>{i + 1}</td>
                      <td style={td}>
                        <span style={{ fontWeight: 600, color: "var(--text-0)" }}>{op.name}</span>
                        {op.code && <span style={{ marginLeft: 8, fontFamily: "monospace", fontSize: 11, color: "var(--text-2)" }}>{op.code}</span>}
                        {op.active === false && <span style={{ marginLeft: 8, fontSize: 11, color: "var(--warn)" }}>inactive</span>}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ color: "var(--text-2)" }}>₹</span>
                          <input type="number" step="0.01" min="0" value={rates[id] ?? ""} placeholder="0.00"
                            onChange={e => setRates(p => ({ ...p, [id]: e.target.value }))}
                            style={{
                              width: 110, padding: "5px 8px", borderRadius: 6, fontSize: 13,
                              fontFamily: "monospace", fontWeight: 700, outline: "none",
                              border: `1px solid ${bad ? "var(--danger)" : "var(--border)"}`,
                              background: "var(--bg-card)", color: "var(--text-0)",
                            }} />
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        <button type="button" title="Move up" disabled={i === 0} onClick={() => move(i, -1)} style={iconBtn(i === 0)}>
                          <ArrowUp size={14} />
                        </button>
                        <button type="button" title="Move down" disabled={i === seq.length - 1} onClick={() => move(i, 1)}
                          style={iconBtn(i === seq.length - 1)}>
                          <ArrowDown size={14} />
                        </button>
                        <button type="button" title="Remove" onClick={() => remove(id)}
                          style={{ ...iconBtn(false), color: "var(--danger)" }}>
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ ...sectionLabel, marginTop: 14 }}>Add operation</div>
          {ops.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              No active operations — create them in Stitching Operations first.
            </div>
          ) : available.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>All operations are linked.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {available.map(op => (
                <button key={op.id} type="button" onClick={() => add(op)} style={chip}>
                  <Plus size={12} /> {op.operationName}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// CSS variables, so both themes read correctly.
const th = {
  textAlign: "left", padding: "8px 12px", fontSize: 12, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-2)",
};
const td = { padding: "8px 12px", color: "var(--text-0)", verticalAlign: "middle" };
const sectionLabel = {
  fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em",
  color: "var(--text-2)", marginBottom: 6,
};
const chip = {
  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
  padding: "4px 10px", borderRadius: 14, fontSize: 12.5, fontWeight: 600,
  border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-0)",
};
const iconBtn = disabled => ({
  border: "none", background: "none", padding: "3px 5px", borderRadius: 5,
  cursor: disabled ? "default" : "pointer", color: disabled ? "var(--border)" : "var(--text-1)",
  display: "inline-flex", alignItems: "center", verticalAlign: "middle",
});
